import { z } from 'zod';

type RefinementCtx = z.RefinementCtx;

/**
 * Validates the hourly_premium surcharge/method fields.
 *
 * Used when a schema has a type/value pair for an hourly premium and the
 * active mode is 'hourly_premium'. The caller provides the exact path strings
 * to use in issue reports (field names differ across schemas).
 *
 * Conditions (exact, matching all four schemas):
 *   - premiumType missing  → "Premium Type is required" at typePath
 *   - premiumType present but premiumValue missing/≤0 → "Premium value must be greater than 0" at valuePath
 */
export function addHourlyPremiumIssues(
  ctx: RefinementCtx,
  premiumType: string | null | undefined,
  premiumValue: number | null | undefined,
  typePath: string,
  valuePath: string
): void {
  if (!premiumType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [typePath],
      message: 'Premium Type is required',
    });
  }
  if (premiumType && (!premiumValue || premiumValue <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [valuePath],
      message: 'Premium value must be greater than 0',
    });
  }
}

/**
 * Adds an issue if a fixed dollar amount is null/undefined/≤0.
 */
export function addPositiveAmountIssue(
  ctx: RefinementCtx,
  value: number | null | undefined,
  path: string,
  message: string
): void {
  if (value == null || value <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message,
    });
  }
}

/**
 * Adds an issue if a percentage value is outside [min, max].
 * Defaults: min = 0.01, max = 100.
 */
export function addPercentageIssue(
  ctx: RefinementCtx,
  value: number | null | undefined,
  path: string,
  message: string,
  min = 0.01,
  max = 100
): void {
  if (value == null || value < min || value > max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message,
    });
  }
}
